<template>
	<Teleport to="body">
		<div class="modal-overlay" @click="emit('close')">
			<div class="modal-content" @click.stop>
				<div class="modal-header">
					<h3>
						{{ t("transactions-table.edit-modal.title") }} —
						{{ formatInscriptionNumber(transaction.inscriptionNumber) }}
					</h3>
					<button type="button" class="close-btn" :title="t('callToActions.close')" @click="emit('close')">
						<AppIcon :name="IconName.CloseSmall" :size="16" :stroke-width="1.3" />
					</button>
				</div>

				<div class="modal-body">
					<section class="field-group">
						<h4>{{ t("transactions-table.sections.localisation") }}</h4>
						<div class="lots-field">
							<span class="lots-title-row">
								<span class="lots-title">{{ t("transactions-table.columns.lots") }}</span>
								<button
									v-if="dedupedLotNumbers.length > 1"
									type="button"
									class="parcels-toggle"
									@click="lotsExpanded = !lotsExpanded"
								>
									{{ lotsExpanded ? t("parcels.hide") : t("parcels.showAll") }}
								</button>
							</span>
							<span class="lots-value">
								<span v-if="dedupedLotNumbers.length" class="lots-value-text parcels">
									<span class="primary-parcel">{{ dedupedLotNumbers[0] }}</span>
									<span v-if="!lotsExpanded && dedupedLotNumbers.length > 1" class="parcels-count">
										+{{ dedupedLotNumbers.length - 1 }}
									</span>
									<span v-if="lotsExpanded && dedupedLotNumbers.length > 1" class="parcels-rest">
										, {{ dedupedLotNumbers.slice(1).join(", ") }}
									</span>
								</span>
								<span v-else class="lots-value-text empty">—</span>
							</span>
						</div>
						<EditableField
							class="wide-field"
							type="text"
							:title="t('transactions-table.columns.address')"
							:name="TransactionEditField.Address"
							:value="transaction.location?.address"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="text"
							:title="t('transactions-table.columns.city')"
							:name="TransactionEditField.MunicipalityName"
							:value="transaction.location?.municipality"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
					</section>

					<section class="field-group">
						<h4>{{ t("transactions-table.sections.date-parties") }}</h4>
						<EditableField
							type="date"
							:title="t('transactions-table.columns.sale-date')"
							:name="TransactionEditField.DeedOfSaleDate"
							:value="transaction.deedOfSaleDate"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="date"
							:title="t('transactions-table.columns.promise-date')"
							:name="TransactionEditField.PsaDate"
							:value="transaction.psaDate"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.sale-context')"
							name="saleContext"
							:options="TransactionSaleContext"
							:value="transaction.saleContext"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="text"
							:title="t('transactions-table.columns.buyer')"
							:name="TransactionEditField.Buyer"
							:value="transaction.buyer?.name"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="text"
							:title="t('transactions-table.columns.buyer-reps')"
							name="buyerReps"
							:value="
								[transaction.buyerRepresentativeFirstName, transaction.buyerRepresentativeLastName]
									.filter(Boolean)
									.join(' ')
							"
						/>
						<EditableField
							type="text"
							:title="t('transactions-table.columns.seller')"
							:name="TransactionEditField.Seller"
							:value="transaction.seller?.name"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="text"
							:title="t('transactions-table.columns.seller-reps')"
							name="sellerReps"
							:value="
								[transaction.sellerRepresentativeFirstName, transaction.sellerRepresentativeLastName]
									.filter(Boolean)
									.join(' ')
							"
						/>
					</section>

					<section class="field-group">
						<h4>{{ t("transactions-table.sections.type-transaction") }}</h4>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.asset-type')"
							:name="TransactionEditField.AssetType"
							:options="assetTypeOptions"
							:multiple="true"
							:value="transaction.assetType || []"
							@input="onClassificationUpdate"
							:disabled="!isWriter"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.land-category')"
							:name="TransactionEditField.LandCategoryNew"
							:options="landCategoryOptions"
							:multiple="true"
							:value="transaction.landCategoryNew || []"
							@input="onClassificationUpdate"
							:disabled="!isWriter || !hasLandAssetType"
						/>
						<EditableField
							readonly
							type="text"
							:title="t('transactions-table.columns.authorized-uses')"
							name="authorizedUses"
							:value="transaction.authorizedUses"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.current-state')"
							name="currentState"
							:options="TransactionCurrentState"
							:value="transaction.currentState"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="checkbox"
							:title="t('transactions-table.columns.tod-zone')"
							name="tod"
							:value="transaction.tod"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="1"
							:title="areaFieldTitle('land-area')"
							:symbol="areaUnitSymbol"
							:name="TransactionAreaField.TotalLandArea"
							:value="transactionAreaValue(transaction, TransactionAreaField.TotalLandArea, areaUnit)"
						/>
					</section>

					<section class="field-group">
						<h4>{{ t("transactions-table.sections.prix") }}</h4>
						<EditableField
							type="number"
							:rounding="0.01"
							:title="t('transactions-table.columns.purchase-price')"
							symbol="$"
							name="salePrice"
							:value="transaction.salePrice"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="1"
							symbol="$"
							:title="t('transactions-table.columns.adjusted-price')"
							name="adjustedPrice"
							:value="adjustedPrice"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="0.01"
							:symbol="pricePerAreaUnitSymbol"
							:title="pricePerAreaFieldTitle('price-per-sqft')"
							:name="TransactionPricePerAreaField.Land"
							:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Land, areaUnit)"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="0.01"
							:symbol="pricePerAreaUnitSymbol"
							:title="pricePerAreaFieldTitle('price-per-buildable-sqft')"
							:name="TransactionPricePerAreaField.Buildable"
							:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Buildable, areaUnit)"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="100"
							symbol="$"
							:title="t('transactions-table.columns.price-per-unit')"
							name="pricePerUnit"
							:value="transaction.pricePerUnit"
						/>
					</section>

					<section class="field-group">
						<h4>{{ t("transactions-table.sections.developpement") }}</h4>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.codification-type')"
							name="codificationType"
							:options="TransactionCodificationType"
							:value="transaction.codificationType"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.approval-type')"
							name="projectApproval"
							:options="TransactionProjectApproval"
							:value="transaction.projectApproval"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.approval-status')"
							name="projectApprovalAtDeedOfSale"
							:options="TransactionProjectApprovalAtDeedOfSale"
							:value="transaction.projectApprovalAtDeedOfSale"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="url"
							:title="t('transactions-table.columns.project-document')"
							name="projectDocument"
							:value="transaction.projectDocument"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="url"
							:title="t('transactions-table.columns.project-link')"
							name="projectLink"
							:value="transaction.projectLink"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="url"
							:title="t('transactions-table.columns.model-link')"
							name="volumetricImageUrl"
							:value="transaction.volumetricImageUrl"
						/>
						<EditableField
							type="select"
							:title="t('transactions-table.columns.land-subcategory')"
							:name="TransactionEditField.LandSubcategory"
							:options="landSubcategoryOptions"
							:multiple="true"
							:value="transaction.landSubcategory || []"
							@input="onClassificationUpdate"
							:disabled="!isWriter || !hasRelevantLandCategory"
						/>
						<EditableField
							type="number"
							:rounding="1"
							:title="t('transactions-table.columns.floor-count')"
							name="stories"
							:value="transaction.stories"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="number"
							:rounding="0.1"
							symbol="%"
							:title="t('transactions-table.columns.implantation-rate')"
							name="siteCoverageRatio"
							:value="transaction.siteCoverageRatio"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="0.000001"
							:title="t('transactions-table.columns.density')"
							name="density"
							:value="transaction.density"
						/>
						<EditableField
							type="number"
							:rounding="1"
							:symbol="areaUnitSymbol"
							:title="areaFieldTitle('buildable-area')"
							:name="TransactionAreaField.BuildableArea"
							:value="transactionAreaValue(transaction, TransactionAreaField.BuildableArea, areaUnit)"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="number"
							:rounding="1"
							:title="t('transactions-table.columns.unit-count')"
							name="numberOfUnits"
							:value="transaction.numberOfUnits"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="number"
							:rounding="1"
							:title="t('transactions-table.columns.outdoor-parking')"
							name="parkingOutdoorCount"
							:value="transaction.parkingOutdoorCount"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							type="number"
							:rounding="1"
							:title="t('transactions-table.columns.indoor-parking')"
							name="parkingIndoorCount"
							:value="transaction.parkingIndoorCount"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="1"
							:title="t('transactions-table.columns.total-parking')"
							name="parkingTotal"
							:value="transaction.parkingTotal"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="0.1"
							:title="t('transactions-table.columns.parking-ratio')"
							name="parkingRatio"
							:value="transaction.parkingRatio"
						/>
						<EditableField
							class="wide-field"
							type="textarea"
							:title="t('transactions-table.columns.project-comments')"
							name="projectComments"
							:value="transaction.projectComments"
							@input="onFieldInput"
							:disabled="!isWriter"
						/>
						<EditableField
							readonly
							type="text"
							:title="t('transactions-table.columns.residential-unit-type')"
							name="unitType"
							:value="transaction.unitType"
						/>
						<EditableField
							readonly
							type="number"
							:rounding="1"
							:symbol="areaUnitSymbol"
							:title="areaFieldTitle('avg-unit-area')"
							:name="TransactionAreaField.UnitArea"
							:value="transactionAreaValue(transaction, TransactionAreaField.UnitArea, areaUnit)"
						/>
					</section>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { UseRootStore, Permission } from "@/model/RootStore"
import {
	type Transaction,
	TransactionSaleContext,
	TransactionCurrentState,
	TransactionCodificationType,
	TransactionProjectApproval,
	TransactionProjectApprovalAtDeedOfSale,
} from "@/model/DataModel"
import { AssetType, LandCategory, LandSubcategory } from "@/graphql"
import { formatInscriptionNumber } from "@/utils/format"
import { parseISODate } from "@/utils/date"
import { useUnitSystem } from "@/composables/useUnitSystem"
import {
	TransactionAreaField,
	transactionAreaInputValue,
	transactionAreaValue,
	TransactionPricePerAreaField,
	transactionPricePerAreaValue,
} from "@/utils/transactionUnitFields"
import EditableField from "./EditableField.vue"

enum TransactionEditField {
	Address = "address",
	MunicipalityName = "municipalityName",
	Buyer = "buyer",
	Seller = "seller",
	PsaDate = "psaDate",
	DeedOfSaleDate = "deedOfSaleDate",
	AssetType = "assetType",
	LandCategoryNew = "landCategoryNew",
	LandSubcategory = "landSubcategory",
}

const transaction = defineModel<Transaction>("transaction", { required: true })

const emit = defineEmits<{
	(e: "close"): void
}>()

const { t } = useI18n()
const store = UseRootStore()
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()

const isWriter = computed(() => !!store.currentUser?.has(Permission.TransactionWrite))

function areaFieldTitle(field: string): string {
	return `${t(`transactions-table.unit-columns.${field}`)} (${areaUnitSymbol.value})`
}

function pricePerAreaFieldTitle(field: string): string {
	return `${pricePerAreaUnitSymbol.value} ${t(`transactions-table.unit-columns.${field}`)}`
}

const lotsExpanded = ref(false)
// parcelNumbers can repeat a lot number; dedupe so the count/expanded list don't double-count it
const dedupedLotNumbers = computed(() =>
	Array.from(new Set(transaction.value.location.parcelNumbers?.filter((n): n is string => !!n) ?? []))
)

// mirrors TransactionsTable.vue's computeAdjustedPrice: this isn't a real transaction field,
// just a client-side estimate of the sale price adjusted to today's value
const ANNUAL_PRICE_ADJUSTMENT_RATE = 0.03
const adjustedPrice = computed<number | null>(() => {
	const tr = transaction.value
	if (tr.salePrice == null) return null
	const date = tr.psaDate ?? tr.deedOfSaleDate
	if (date == null) return null
	const yearsElapsed = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 365)
	return Math.round((tr.salePrice * Math.pow(1 + ANNUAL_PRICE_ADJUSTMENT_RATE, yearsElapsed)) / 10000) * 10000
})

// mirrors TransactionCardDetailed.vue's date coercion for EditableField's date picker
function toLocalDateValue(value: unknown): Date | null | unknown {
	if (typeof value !== "string") return value
	return /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISODate(value) : value
}

function applyLocalFieldInput(name: string, value: any): void {
	switch (name) {
		case TransactionEditField.Address:
			transaction.value.location = { ...transaction.value.location, address: value }
			return
		case TransactionEditField.MunicipalityName:
			transaction.value.location = { ...transaction.value.location, municipality: value }
			return
		case TransactionEditField.Buyer:
			transaction.value.buyer = { ...transaction.value.buyer, name: value }
			return
		case TransactionEditField.Seller:
			transaction.value.seller = { ...transaction.value.seller, name: value }
			return
		case TransactionEditField.PsaDate:
		case TransactionEditField.DeedOfSaleDate:
			;(transaction.value as any)[name] = toLocalDateValue(value)
			return
		default:
			;(transaction.value as any)[name] = value
	}
}

async function onFieldInput({ name, value }: { name: string; value: any }): Promise<void> {
	if (!isWriter.value) return

	const { inscriptionNumber } = transaction.value
	if (inscriptionNumber == null) return

	const sourceValue = transactionAreaInputValue(name, value, areaUnit.value)

	applyLocalFieldInput(name, sourceValue)
	await store.updateTransaction(inscriptionNumber, { [name]: sourceValue })
}

// mirrors TransactionCardDetailed.vue's onClassificationUpdate: landCategoryNew/landSubcategory imply asset_type "Land"
async function onClassificationUpdate({ name, value }: { name: string; value: any }): Promise<void> {
	if (!isWriter.value) return

	const { inscriptionNumber } = transaction.value
	if (inscriptionNumber == null) return

	if (name === TransactionEditField.LandCategoryNew && value?.length > 0) {
		const currentAssetType = transaction.value.assetType || []
		const hasLand = currentAssetType.some(v => v === AssetType.Land)
		const assetTypeToSend = hasLand ? currentAssetType : [...currentAssetType, AssetType.Land]

		transaction.value.landCategoryNew = value
		if (!hasLand) transaction.value.assetType = assetTypeToSend

		await store.updateTransaction(inscriptionNumber, { assetType: assetTypeToSend, landCategoryNew: value })
		return
	}

	if (name === TransactionEditField.LandSubcategory && value?.length > 0) {
		const currentLandCategory = transaction.value.landCategoryNew || []
		const currentAssetType = transaction.value.assetType || []
		const hasLand = currentAssetType.some(v => v === AssetType.Land)
		const assetTypeToSend = hasLand ? currentAssetType : [...currentAssetType, AssetType.Land]

		transaction.value.landSubcategory = value
		if (!hasLand) transaction.value.assetType = assetTypeToSend

		await store.updateTransaction(inscriptionNumber, {
			assetType: assetTypeToSend,
			landCategoryNew: currentLandCategory,
			landSubcategory: value,
		})
		return
	}

	if (name === TransactionEditField.AssetType) {
		const hasLand = value?.some((v: string | null | undefined) => v === AssetType.Land)
		if (!hasLand && (transaction.value.landCategoryNew?.length || transaction.value.landSubcategory?.length)) {
			transaction.value.landCategoryNew = []
			transaction.value.landSubcategory = []
			await store.updateTransaction(inscriptionNumber, { assetType: value, landCategoryNew: [], landSubcategory: [] })
			return
		}
	}

	;(transaction.value as any)[name] = value
	await store.updateTransaction(inscriptionNumber, { [name]: value })
}

const hasLandAssetType = computed(() => (transaction.value.assetType || []).some(type => type === AssetType.Land))

const hasRelevantLandCategory = computed(() => {
	if (!transaction.value.landCategoryNew?.length) return false

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return transaction.value.landCategoryNew.some(cat => cat != null && relevantCategories.includes(cat as LandCategory))
})

const assetTypeOptions = computed(() =>
	Object.values(AssetType).map(value => ({ value, label: t(`property.assetType.${value}`) }))
)

const landCategoryOptions = computed(() =>
	Object.values(LandCategory).map(value => ({ value, label: t(`property.landCategory.${value}`) }))
)

// mirrors TransactionCardDetailed.vue's landSubcategoryOptions
const landSubcategoryOptions = computed(() => {
	const selectedCategories = transaction.value.landCategoryNew || []
	const allOptions: { value: string; label: string }[] = []

	if (selectedCategories.includes(LandCategory.Residential)) {
		allOptions.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") },
			{ value: LandSubcategory.TownHouse, label: t("property.landSubcategory.TownHouse") },
			{ value: LandSubcategory.SingleFamily, label: t("property.landSubcategory.SingleFamily") }
		)
	}

	if (selectedCategories.includes(LandCategory.Commercial)) {
		allOptions.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") }
		)
	}

	if (selectedCategories.includes(LandCategory.Industrial)) {
		allOptions.push(
			{ value: LandSubcategory.LightMedium, label: t("property.landSubcategory.LightMedium") },
			{ value: LandSubcategory.Heavy, label: t("property.landSubcategory.Heavy") }
		)
	}

	if (selectedCategories.includes(LandCategory.Agricultural)) {
		allOptions.push(
			{ value: LandSubcategory.FullyCultivated, label: t("property.landSubcategory.FullyCultivated") },
			{ value: LandSubcategory.MostlyCultivated, label: t("property.landSubcategory.MostlyCultivated") },
			{ value: LandSubcategory.HalfAndHalf, label: t("property.landSubcategory.HalfAndHalf") },
			{ value: LandSubcategory.MostlyForestry, label: t("property.landSubcategory.MostlyForestry") },
			{ value: LandSubcategory.FullForestry, label: t("property.landSubcategory.FullForestry") }
		)
	}

	const seen = new Set<string>()
	return allOptions.filter(option => {
		if (seen.has(option.value)) return false
		seen.add(option.value)
		return true
	})
})
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	backdrop-filter: blur(4px);
}

.modal-content {
	background: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	width: 90%;
	max-width: 1160px;
	max-height: 85vh;
	display: flex;
	flex-direction: column;
	box-shadow: var(--shadow-xl);
	overflow: hidden;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: var(--spacing-sm);
	padding: var(--spacing-lg);
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
	flex-shrink: 0;

	h3 {
		margin: 0;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-semibold);
	}

	.close-btn {
		flex-shrink: 0;
		border: none;
		background: none;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--neutral-600);
		cursor: pointer;
		transition: color 0.2s ease;

		&:hover {
			color: var(--neutral-900);
		}
	}
}

.modal-body {
	overflow-y: auto;
	padding: var(--spacing-lg);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
}

.field-group {
	display: grid;
	align-items: flex-start;
	grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
	gap: var(--spacing-lg) var(--spacing-xl);
	padding-bottom: var(--spacing-lg);
	border-bottom: var(--border-width-thin) solid var(--neutral-100);

	&:last-child {
		padding-bottom: 0;
		border-bottom: none;
	}

	h4 {
		grid-column: 1 / -1;
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.wide-field {
		grid-column: 1 / -1;
	}
}

.field-group :deep(.editable-field) {
	flex-flow: row wrap !important;
	align-items: center !important;
	gap: var(--space-4) var(--spacing-xs) !important;
	padding: 0 0 var(--spacing-xs) !important;
	border-bottom: none !important;
}

.field-group :deep(.editable-field .title),
.field-group :deep(.editable-field .hover-card-wrapper) {
	flex: 1 0 100% !important;
	align-self: flex-start !important;
}

.field-group :deep(.editable-field .value) {
	justify-content: flex-start !important;
	align-self: center !important;
	flex: 1 1 auto !important;
	min-width: 0 !important;
}

.field-group :deep(.editable-field .symbol) {
	flex: 0 0 auto !important;
}

.field-group :deep(.editable-field .value-text) {
	text-align: left !important;
}

.field-group :deep(.editable-field.is-readonly .value-text) {
	display: flex;
	align-items: center;
	height: var(--spacing-xl);
	padding: 0 var(--spacing-xs);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	background-color: var(--neutral-50);
	box-sizing: border-box;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.lots-field {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: var(--spacing-xxs);
	padding: 0 0 var(--spacing-xs);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);

	.lots-title-row {
		display: flex;
		align-items: baseline;
		gap: var(--spacing-sm);
	}

	.lots-title {
		font-weight: var(--font-weight-medium);
		color: var(--neutral-600);
	}

	.parcels-toggle {
		position: relative;
		top: -1px;
		background: none;
		border: none;
		padding: 0;
		font: inherit;
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-600);
		cursor: pointer;

		&:hover {
			color: var(--primary-green);
		}
	}

	.lots-value {
		display: flex;
		justify-content: flex-start;
		overflow: hidden;
	}

	.lots-value-text {
		width: 100%;
		color: var(--neutral-900);
		text-align: left;
		line-height: var(--line-height-normal);
		overflow-wrap: break-word;

		&.empty {
			color: var(--neutral-400);
		}
	}

	.parcels-count {
		font-size: var(--font-size-xxs);
		color: var(--neutral-600);
	}
}
</style>
